/**
 * 서울시 실시간 도시데이터 API 서비스
 * 인증키: 634a4a5a6a64616537354547514c56
 * 엔드포인트: http://openapi.seoul.go.kr:8088/{KEY}/json/citydata/1/1/{AREA_NM}
 */

const API_KEY = '634a4a5a6a64616537354547514c56';
const BASE_URL = 'http://openapi.seoul.go.kr:8088';

/**
 * 특정 거점의 실시간 도시데이터 조회
 * @param {string} areaName - 거점명 (예: '강남역')
 * @returns {Promise<Object>} 혼잡도 데이터
 */
export async function fetchCityData(areaName) {
  const encodedName = encodeURIComponent(areaName);
  const url = `${BASE_URL}/${API_KEY}/json/citydata/1/1/${encodedName}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`API 호출 실패: ${response.status}`);
  }
  const data = await response.json();

  if (!data.CITYDATA) {
    throw new Error('데이터 없음');
  }

  return parseCityData(data.CITYDATA);
}

/**
 * 여러 거점 데이터를 병렬 조회 (최대 5개씩 배치)
 * @param {Array} spots - 거점 배열 [{name, id, lat, lng}]
 * @returns {Promise<Map>} spot.id -> 혼잡도 데이터 맵
 */
export async function fetchMultipleCityData(spots) {
  const results = new Map();
  const BATCH_SIZE = 5;

  for (let i = 0; i < spots.length; i += BATCH_SIZE) {
    const batch = spots.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.allSettled(
      batch.map(spot =>
        fetchCityData(spot.name).then(data => ({ id: spot.id, data }))
      )
    );

    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        results.set(result.value.id, result.value.data);
      }
    });

    // Rate limit 방지: 배치 간 500ms 대기
    if (i + BATCH_SIZE < spots.length) {
      await new Promise(r => setTimeout(r, 500));
    }
  }

  return results;
}

/**
 * API 응답 데이터 파싱
 * @param {Object} citydata - CITYDATA 객체
 * @returns {Object} 정제된 혼잡도 데이터
 */
function parseCityData(citydata) {
  const ppltn = citydata.LIVE_PPLTN_STTS?.[0] || {};
  const weather = citydata.WEATHER_STTS?.[0] || {};
  const road = citydata.ROAD_TRAFFIC_STTS?.AVG_ROAD_DATA || {};

  return {
    areaName: citydata.AREA_NM,
    areaCode: citydata.AREA_CD,

    // 혼잡도 등급 매핑 (텍스트 -> 숫자)
    congestionLevel: (() => {
      const lvl = ppltn.AREA_CONGEST_LVL;
      if (lvl === '여유') return 1;
      if (lvl === '보통') return 2;
      if (lvl === '약간 붐빔') return 3;
      if (lvl === '붐빔') return 4;
      return 1; // 기본값
    })(),
    congestionLevelText: ppltn.AREA_CONGEST_LVL || '정보없음',
    congestionMessage: ppltn.AREA_CONGEST_MSG || '',

    // 인구 추정
    populationMin: parseInt(ppltn.AREA_PPLTN_MIN || '0'),
    populationMax: parseInt(ppltn.AREA_PPLTN_MAX || '0'),

    // 성별 비율
    maleRate: parseFloat(ppltn.MALE_PPLTN_RATE || '0'),
    femaleRate: parseFloat(ppltn.FEMALE_PPLTN_RATE || '0'),

    // 연령대 비율
    ageRates: {
      age10: parseFloat(ppltn.PPLTN_RATE_10 || '0'),
      age20: parseFloat(ppltn.PPLTN_RATE_20 || '0'),
      age30: parseFloat(ppltn.PPLTN_RATE_30 || '0'),
      age40: parseFloat(ppltn.PPLTN_RATE_40 || '0'),
      age50: parseFloat(ppltn.PPLTN_RATE_50 || '0'),
      age60: parseFloat(ppltn.PPLTN_RATE_60 || '0'),
    },

    // 거주자/비거주자
    residentRate: parseFloat(ppltn.RESNT_PPLTN_RATE || '0'),
    nonResidentRate: parseFloat(ppltn.NON_RESNT_PPLTN_RATE || '0'),

    // 업데이트 시간
    updatedAt: ppltn.PPLTN_TIME || '',

    // 예보 (향후 12시간)
    forecast: (ppltn.FCST_PPLTN || []).map(f => ({
      time: f.FCST_TIME,
      level: f.FCST_CONGEST_LVL,
      min: parseInt(f.FCST_PPLTN_MIN || '0'),
      max: parseInt(f.FCST_PPLTN_MAX || '0'),
    })),

    // 날씨
    weather: {
      temp: weather.TEMP || '-',
      humidity: weather.HUMIDITY || '-',
      windSpeed: weather.WIND_SPD || '-',
      pm10Index: weather.PM10_INDEX || '-',
      airIndex: weather.AIR_IDX || '-',
      uvLevel: weather.UV_INDEX_LVL || '-',
    },

    // 교통
    traffic: {
      message: road.ROAD_MSG || '',
      index: road.ROAD_TRAFFIC_IDX || '',
      speed: road.ROAD_TRAFFIC_SPD || 0,
    },
    
    // 행사 정보 추가
    // API 키: EVENT_STTS (EVENT_STN 아님), 좌표: EVENT_X/EVENT_Y 포함
    events: (citydata.EVENT_STTS || []).map(e => ({
      name: e.EVENT_NM,
      period: e.EVENT_PERIOD,
      place: e.EVENT_PLACE,
      lat: e.EVENT_Y || null,
      lng: e.EVENT_X || null,
      isFree: e.PAY_YN === '무료' || e.PAY_YN === null, // null이면 무료로 간주
      thumbnail: e.THUMBNAIL || '',
      url: e.URL || '',
      etc: e.EVENT_ETC_DETAIL || ''
    }))
  };
}

/**
 * 로컬 개발 환경용 프록시 호출 함수 (1개 지역 조회)
 */
async function fetchCityDataProxy(areaName) {
  const encodedName = encodeURIComponent(areaName);
  const url = `/api/seoul/${API_KEY}/json/citydata/1/1/${encodedName}`;

  const response = await fetch(url);
  if (!response.ok) throw new Error(`API 호출 실패: ${response.status}`);
  const data = await response.json();

  if (!data.CITYDATA) throw new Error('데이터 없음');
  return parseCityData(data.CITYDATA);
}

/**
 * Vercel Serverless Function을 호출하여 한 번에 115개 전체 장소의 혼잡도 데이터를 가져옵니다.
 * (Vercel Edge Request 한도 초과 방지 및 5분 캐싱 적용)
 */
export async function fetchAllCongestionData() {
  // 1. 로컬 개발 환경(Vite)에서는 기존처럼 프록시를 통해 클라이언트에서 수집
  if (import.meta.env && import.meta.env.DEV) {
    const { SEOUL_SPOTS } = await import('../data/seoulSpots.js');
    const newMap = new Map();
    await Promise.allSettled(
      SEOUL_SPOTS.map(async (spot) => {
        try {
          const data = await fetchCityDataProxy(spot.name);
          newMap.set(spot.id, data);
        } catch (e) {
          newMap.set(spot.id, { congestionLevel: 0, congestionLevelText: '정보없음' });
        }
      })
    );
    return newMap;
  }

  // 2. 실제 Vercel 운영 환경 및 모바일 앱(Capacitor)에서는 단일 백엔드 API 엔드포인트 호출
  // Capacitor 환경에서는 /api/congestion이 로컬호스트를 가리키므로 반드시 Vercel 서버의 절대 주소를 명시해야 합니다.
  const url = `https://ansim-in-seoul.vercel.app/api/congestion`;
  
  const response = await fetch(url);
  if (!response.ok) throw new Error(`API 호출 실패: ${response.status}`);
  
  const data = await response.json();
  
  // JSON 객체를 Map 형태로 변환 (HomePage.jsx에서 기존 Map 구조를 사용하므로)
  const results = new Map();
  Object.keys(data).forEach(id => {
    results.set(id, data[id]);
  });
  
  return results;
}
